import type { ComputedRef, Ref } from "vue";
import { computed, onMounted, ref, watch } from "vue";

import { boardActions } from "@/action/boardActions";
import { cardActions } from "@/action/cardActions";
import { internalActions } from "@/action/internalActions";
import type StickyNoteText from "@/components/StickyNote/internal/StickyNoteText/StickyNoteText.vue";
import { TEXT_INPUT_WRAPPER_CLASS } from "@/components/StickyNote/internal/StickyNoteText/StickyNoteText.vue";
import { useDelayedAction } from "@/composables/useDelayedAction";
import type { StickyNoteFocus } from "@/composables/useEventBus";
import { sendStickyNoteAction, useEventBus } from "@/composables/useEventBus";
import { byPosition, skipButtonId } from "@/directives/ownedCards";
import { longClick } from "@/model/Settings";
import type { Board } from "@/model/board";
import { isBacklogBoard } from "@/model/board";
import type { Card, CardMeta } from "@/model/card";
import { useBoardStore } from "@/store/board";
import { useDraggingStore } from "@/store/dragging";
import { usePanModeStore } from "@/store/panMode";
import { useSelectionStore } from "@/store/selection";
import { eventTarget, isLinkDrag } from "@/utils/dom/dom";
import { isKeyDown } from "@/utils/shortcut/shortcuts";

export interface Props {
  el: Ref<HTMLDivElement | null>;
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents -- importing vue components results in `any`
  textInput: Ref<typeof StickyNoteText | null>;
  card: Card;
  board: Ref<Board>;
  cardMeta: CardMeta;
  disabled?: Ref<boolean>;
}

interface UseSelectReturn {
  isSelected: ComputedRef<Readonly<boolean>>;
  deactivateSticky: () => void;
  tabindex: number;
  wasActivatedByKeyboard: Ref<boolean>;
}

export function useSelect({
  el,
  textInput,
  board,
  card,
  cardMeta,
  disabled,
}: Props): UseSelectReturn {
  const wasDragged = ref(false);
  const allowSelectionAfterDrag = ref(false);

  const panModeClickCount = ref(0);

  // Tracks whether the sticky note was activated by keyboard
  const wasActivatedByKeyboard = ref(false);

  // Props for managing cards' tab order within board sections (on non-backlog boards)
  const managedTabOrder = computed(() => !isBacklogBoard(board.value?.type));
  const nextTabTarget = ref<HTMLElement | null>(null);
  const previousTabTarget = ref<HTMLElement | null>(null);

  const boardStore = useBoardStore();
  const dragStore = useDraggingStore();

  const isSelected = computed(() => boardStore.isStickySelected(card.id));
  const isActive = computed(() => boardStore.activeCardId === card.id);

  // Used to cancel the selection of the sticky note when the user
  // holds the left mouse button pressed on the board for more than 500ms
  const wasLongPressed = ref(false);
  const [setWasLongPressed, cancelSetWasLongPressed] = useDelayedAction(() => {
    if (wasDragged.value) return;
    wasLongPressed.value = true;
  }, longClick);

  onMounted(() => {
    if (disabled?.value) return;

    // event listeners for selecting the sticky note
    el.value?.addEventListener("click", handleStickyNoteClick);
    el.value?.addEventListener("pointerdown", handlePointerDown);
    el.value?.addEventListener("keydown", handleKeyDown);
    el.value?.addEventListener("focusin", handleFocusIn);

    const { onStickyNoteAction } = useEventBus();
    // listen for focus action from event bus (used for highlighting sticky from URL)
    onStickyNoteAction(null, "focus", handleFocusAction);
    onStickyNoteAction(card.id, "deactivate", cleanup);
  });

  const handleFocusAction = (action: StickyNoteFocus) => {
    const isActionForThisCard = action.id === card.id || action.id === card.groupId;
    if (isActionForThisCard) {
      el.value?.focus();
      el.value?.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    }
  };

  // Track when the sticky note is being dragged
  // External drags (palette & issue selector) should allow selection after drag
  watch(
    () => dragStore.dragging[card.id],
    (drag) => {
      // grabFromOutside: Indicates the card was dragged from an external source (palette, issue selector)
      // rather than being repositioned on the board.
      //
      // When true: The drag originated from useDragToCreateSticky (e.g., dragging from palette to board).
      //            Allow the first click after drop to activate/select the newly placed card.
      //
      // When false/undefined: The drag is a regular board repositioning.
      //                       Prevent click-to-select after drop to avoid accidental activation
      //                       while the user is just moving the card to a new position.
      if (drag?.grabFromOutside) allowSelectionAfterDrag.value = true;
      if (drag) wasDragged.value = true;
    },
  );

  const handleStickyNoteClick = (event: UIEvent, fromKeyboard?: boolean) => {
    // Track whether the sticky note was activated by keyboard, so focus
    // can be returned to the sticky note after deactivation if needed
    wasActivatedByKeyboard.value = !!fromKeyboard;
    // if the action was already executed, this is a noop
    cancelSetWasLongPressed();

    if (wasLongPressed.value) {
      wasLongPressed.value = false;
      return;
    }

    // select sticky notes with a single click when the user has the
    // single click select mode enabled
    const isSingleClickSelect = useSelectionStore().selecting === "click";
    if (usePanModeStore().active && isSingleClickSelect && !wasDragged.value) {
      panModeClickCount.value =
        useBoardStore().activeCardId !== card.id ? 1 : panModeClickCount.value + 1;

      if (panModeClickCount.value === 1) {
        boardActions.clearCardSelection("mouse");
        boardStore.setActiveCardId(card.id);
        boardActions.toggleCardSelection("mouse", card.id);
      } else {
        panModeClickCount.value = 0;
        // Delay focusing to allow the dblclick event in useEnlarge.ts to register properly.
        // This is doen in order to enlarge a sticky note when preforming a dblclick on the TextEditable.
        // Without this, focusing on the input immediately after the second click
        // can prevent the dblclick event from firing.
        setTimeout(() => {
          textInput.value?.focus();
        }, 0);
      }
      return true;
    }

    // Multi select when the user clicks on the sticky note
    // while holding cmd on MacOS or ctrl on Windows or
    const isMultiSelect = isKeyDown("Meta") || isKeyDown("Shift");
    if (isMultiSelect && boardStore.activeCardId !== card.id) {
      boardStore.setActiveCardId(null);
      boardActions.toggleCardSelection("mouse", card.id);
      return;
    }

    const target = eventTarget(event);

    // don't set the sticky note as active if the
    // user clicks on [data-ignore-click] element
    if (target.closest("[data-ignore-click]")) return;

    // If the button is disabled (eg. in overview modal), ignore click
    if (
      el.value?.getAttribute("aria-disabled") &&
      el.value?.getAttribute("aria-disabled") !== "false"
    ) {
      return;
    }

    // We'll only focus the textInput when the user clicked on the text area
    // (makes it easier for the user to copy/paste the card)
    const clickedInput = !!target.closest(`.${TEXT_INPUT_WRAPPER_CLASS}`);

    if (
      isActive.value &&
      !document.activeElement?.querySelector(".sticky-note-text-input") &&
      clickedInput
    ) {
      textInput.value?.focus();
      return;
    }

    if (isActive.value) return;

    // disable clicks when the X key is down
    if (isKeyDown("KeyX")) return;
    // // clicking was disabled from the outside
    if (disabled?.value) return;

    // handle post-drag selection behavior
    if (wasDragged.value) {
      // Prevent selection for regular card drags on the board
      // Allow selection for external drags (palette & issue selector)
      const shouldPreventSelection = !allowSelectionAfterDrag.value;

      // reset flags
      wasDragged.value = false;
      allowSelectionAfterDrag.value = false;

      if (shouldPreventSelection) return;
    }

    document.addEventListener("pointerdown", handleOutsideClick, true);

    if (clickedInput) {
      textInput.value?.focus();
    }
    cardActions.startAlter("internal", card.id);
    internalActions.cardToFront(card.id);

    boardActions.clearCardSelection("action-menu");

    boardStore.selectSticky(card.id);
    boardStore.setActiveCardId(card.id);
  };

  // Set the activeCardId to null when the user clicks outside the card
  const handleOutsideClick = (event: MouseEvent) => {
    const target = eventTarget(event);

    // the click came from  sticky note
    if (target.closest(`[id="${card.id}"].sticky-note `)) {
      return;
    }

    // we ignore clicks from any html element that has data-ignore-click attribute so the sticky note
    // does not get deselected when we slick on an item which was teleported to the body
    if (target.closest("[data-ignore-click]")) {
      return;
    }

    // the click was outside of a sticky note
    deactivateSticky();
    sendStickyNoteAction(card.id, { action: "shrink" });
    boardActions.clearCardSelection("mouse");
  };

  // used to deselect all selected cards when the user clicks on a sticky note
  // while not holding the cmd or ctrl keys
  const handlePointerDown = (event: PointerEvent) => {
    const target = eventTarget(event);
    if (target.closest("[data-ignore-click]")) return;

    // ignore clicks on the link drag icon
    if (isLinkDrag(event)) return;

    setWasLongPressed();

    const isMultiSelect = isKeyDown("Meta") || isKeyDown("Shift");
    const isSingleClickSelect = useSelectionStore().selecting === "click";
    const isOneOfSelected = boardStore.isStickySelected(card.id);

    if (
      !isSingleClickSelect &&
      !isMultiSelect &&
      !isOneOfSelected &&
      target.closest(".sticky-note")
    ) {
      boardActions.clearCardSelection("action-menu");
      boardStore.setActiveCardId(null);
    }
  };

  /**
   * Handle keyboard events on the sticky note
   * Escape: Deactivate sticky note
   * Enter: Activate sticky note (if not yet active)
   */
  const handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case "Enter":
        // Activate sticky (equivalent to clicking on it)
        if (e.target !== el.value) return;
        e.preventDefault();
        handleStickyNoteClick(e, true);
        break;
      case e.shiftKey && "Tab":
        if (isActive.value || !managedTabOrder.value) return;
        e.preventDefault();
        previousTabTarget.value?.focus();
        break;
      case "Tab":
        if (isActive.value || !managedTabOrder.value) return;
        e.preventDefault();
        nextTabTarget.value?.focus();
        break;
      case "Escape":
        deactivateSticky();
        break;
    }
  };

  /**
   * When the sticky note is focused, deactivate the previous sticky
   * so that its action menu closes
   */
  const handleFocusIn = () => {
    const lastCard = useBoardStore().activeCardId;
    const isMultiSelecting =
      useSelectionStore().selecting !== "no" || isKeyDown("Meta") || isKeyDown("Shift");

    if (lastCard && lastCard !== card?.id && !isMultiSelecting) {
      useBoardStore().setActiveCardId(null);
      useBoardStore().unselectSticky(lastCard);
      // TODO: If the user moves to a different sticky with Shift+Tab, the previous
      // sticky stays selected. This will be fixed when we implement tab-nav on stickies
    }

    // Calculate the next and previous stickies for tab navigation
    updateTabNavTargets();
  };

  /**
   * Trigger deactivation. The actual cleanup runs in the cleanup function, driven by the
   * "deactivate" event that boardStore.setActiveCardId emits on transition. That way
   * any path that swaps activeCardId (mouse, keyboard, store-level) runs the same cleanup.
   */
  const deactivateSticky = () => {
    if (isActive.value) {
      if (wasActivatedByKeyboard.value) {
        el.value?.focus();
        wasActivatedByKeyboard.value = false;
      }
      useBoardStore().setActiveCardId(null);
    } else {
      el.value?.blur();
    }
  };

  const cleanup = () => {
    useBoardStore().unselectSticky(card?.id);
    useBoardStore().setEnlargedStickyNoteId(null);
    document.removeEventListener("pointerdown", handleOutsideClick, true);
    internalActions.stopAlter(card?.id);
  };

  /**
   * Determine the next and previous stickies (within the board section) for tab navigation
   * (tab order doesn't loop, instead it focuses on the section's 'cards-skip-button' button at
   * the beginning and end of the section)
   */
  const updateTabNavTargets = () => {
    if (!board.value || !cardMeta || !managedTabOrder.value) return;

    const location = useBoardStore().boardLocation(cardMeta.pos);

    const cards = location.cards(board.value);
    const sortedCards = cards.sort(byPosition);

    const currentCardIndex = sortedCards.findIndex((c) => c.data.id === card.id);

    const nextTargetId =
      sortedCards.length > currentCardIndex + 1
        ? sortedCards[currentCardIndex + 1].data.id
        : skipButtonId(location);

    const prevTargetId =
      currentCardIndex > 0 ? sortedCards[currentCardIndex - 1].data.id : skipButtonId(location);

    nextTabTarget.value = document.getElementById(nextTargetId) || document.querySelector(".board");
    previousTabTarget.value =
      document.getElementById(prevTargetId) || document.querySelector(".board");
  };

  const tabindex = managedTabOrder.value ? -1 : 0;

  return { isSelected, deactivateSticky, tabindex, wasActivatedByKeyboard };
}
